import {
	CommandBar,
	DefaultButton,
	DetailsList,
	Dialog,
	DialogContent,
	DialogFooter,
	Dropdown,
	IDropdownOption,
	ITag,
	Label,
	MessageBar,
	MessageBarType,
	Panel,
	PrimaryButton,
	ProgressIndicator,
	Selection,
	Spinner,
	Stack,
	TagPicker,
	TextField
} from "@fluentui/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useApiCall, useProvidersWithSpecialties } from "../hooks/apiHooks"
import { getCurrentUserContactId } from "../hooks/generalHooks"
import { IAccount } from "../types/IAccount"
import { Provider, ProviderCRUD, ProviderSpecialty } from "../types/IProvider"

export interface IProviderMangementProps {} // eslint-disable-line

export interface ProviderManagementCommandBarProps {
	practiceId: string
	selectedProvider: Provider | null
	setProviders: React.Dispatch<React.SetStateAction<Provider[]>>
}

export interface ProviderPanelProps {
	provider?: Provider
	practiceId: string
	setProviders: React.Dispatch<React.SetStateAction<Provider[]>>
	setOpen: React.Dispatch<React.SetStateAction<string>>
	edit: boolean
	loading: boolean
	error: any
	specialties?: ProviderSpecialty[]
}

interface ProviderPanelFooterProps extends ProviderPanelProps {
	fields: Partial<Provider>
	loading: boolean
	error: any
}

interface RemoveProviderConfirmationProps {
	selectedProvider: Provider | null
	setShowConfirmation: React.Dispatch<React.SetStateAction<boolean>>
	setProviders: React.Dispatch<React.SetStateAction<Provider[]>>
}

const practiceParser = ({ value }: { value: IAccount[] }): IAccount[] => value.map(p => ({ name: p.name, accountid: p.accountid }) as IAccount).sort((a, b) => a.name.localeCompare(b.name))

export function ProviderManagement(_props: IProviderMangementProps): JSX.Element {
	const {
		loading: practicesLoading,
		error: practicesError,
		data: practices,
		selected: selectedPractice,
		setSelected: setSelectedPractice
	} = useApiCall<IAccount[]>("accounts?$select=name,accountid&$filter=(Microsoft.Dynamics.CRM.ContainValues(PropertyName='cw_accounttype',PropertyValues=['121640006']) and statecode eq 0)", {
		crm: true,
		resultParser: practiceParser
	})
	const {
		providers,
		setProviders,
		loading: providersLoading,
		error: providersError,
		selectedProvider,
		setSelectedProvider
	} = useProvidersWithSpecialties(`&$filter=_cw_account_value eq '${selectedPractice?.accountid}'`, { crm: true, canFetch: !!selectedPractice, refetchOnChanges: [selectedPractice] })
	const selection = useRef(new Selection({ onSelectionChanged: () => setSelectedProvider(selection.current.getSelection()[0] as Provider) }))
	const loading = practicesLoading || providersLoading
	const error = practicesError || providersError

	return (
		<Stack>
			<h1>Provider Management</h1>
			{loading && <ProgressIndicator label={`Loading ${practicesLoading ? "practices" : "providers"}...`} />}
			{!loading && practices?.length > 0 && (
				<Dropdown
					disabled={practicesLoading || practicesError}
					label="Select a Practice"
					selectedKey={selectedPractice?.accountid}
					onChange={(_, v) => setSelectedPractice(practices?.find(p => p.accountid === v.key))}
					options={practices?.map(p => ({ key: p.accountid, text: p.name }))}
				/>
			)}
			{!loading && practices?.length === 0 && (
				<MessageBar messageBarType={MessageBarType.info}>No practices found. Please contact an administrator to set up your account to manage your practice(s).</MessageBar>
			)}
			{error && <MessageBar messageBarType={MessageBarType.error}>{error.message || JSON.stringify(error)}</MessageBar>}
			{selectedPractice && providers && (
				<>
					<ProviderManagementCommandBar practiceId={selectedPractice.accountid} selectedProvider={selectedProvider} setProviders={setProviders} />
					{providers.length === 0 ? (
						<MessageBar messageBarType={MessageBarType.info}>No providers found for the selected practice</MessageBar>
					) : (
						<DetailsList
							selectionMode={1}
							selection={selection.current}
							items={providers}
							columns={[
								{ key: "cw_title", name: "Title", fieldName: "cw_title", minWidth: 100, maxWidth: 100, isResizable: true },
								{ key: "cw_firstname", name: "First Name", fieldName: "cw_firstname", minWidth: 100, maxWidth: 100, isResizable: true },
								{ key: "cw_lastname", name: "Last Name", fieldName: "cw_lastname", minWidth: 100, maxWidth: 100, isResizable: true },
								{ key: "cw_gender", name: "Gender", fieldName: "cw_gender@OData.Community.Display.V1.FormattedValue", minWidth: 75, maxWidth: 75, isResizable: true },
								{ key: "cw_languagesspoken", name: "Languages Spoken", fieldName: "cw_languagesspoken", minWidth: 100, maxWidth: 200, isResizable: true },
								{
									key: "cw_website",
									name: "Website",
									fieldName: "cw_website",
									minWidth: 100,
									maxWidth: 200,
									isResizable: true,
									onRender: item => (
										<a href={item.cw_website} title={item.cw_website}>
											{item.cw_website}
										</a>
									)
								},
								{
									key: "specialties",
									name: "Specialties",
									fieldName: "specialties",
									minWidth: 100,
									maxWidth: 200,
									isResizable: true,
									onRender: item => (
										<div style={{ wordWrap: "break-word", whiteSpace: "pre-wrap" }}>{(item.specialties || []).map((s: ProviderSpecialty) => s.cw_name).join(",")}</div>
									)
								}
							]}
						/>
					)}
				</>
			)}
		</Stack>
	)
}

function ProviderManagementCommandBar(props: ProviderManagementCommandBarProps) {
	const { practiceId, selectedProvider, setProviders } = props
	const [showAddPanel, setShowAddPanel] = useState("")
	const [showConfirmation, setShowConfirmation] = useState(false)
	const { loading, error, data: specialties } = useApiCall<ProviderSpecialty[]>("/specialties")

	return (
		<>
			<CommandBar
				styles={{ primarySet: { justifyContent: "flex-end" } }}
				items={[
					{
						key: "add",
						text: "Add",
						iconProps: { iconName: "Add" },
						onClick: () => setShowAddPanel("add")
					},
					{
						key: "edit",
						text: "Edit",
						disabled: !selectedProvider,
						iconProps: { iconName: "Edit" },
						onClick: () => setShowAddPanel("edit")
					},
					{
						key: "delete",
						text: "Delete",
						disabled: !selectedProvider,
						iconProps: { iconName: "Delete" },
						onClick: () => setShowConfirmation(true)
					}
				]}
			/>
			{showAddPanel && (
				<ProviderPanel
					practiceId={practiceId}
					loading={loading}
					error={error}
					specialties={specialties}
					edit={showAddPanel === "edit"}
					provider={showAddPanel === "edit" ? selectedProvider : null}
					setOpen={setShowAddPanel}
					setProviders={setProviders}
				/>
			)}
			{showConfirmation && <RemoveProviderConfirmation selectedProvider={selectedProvider} setShowConfirmation={setShowConfirmation} setProviders={setProviders} />}
		</>
	)
}

interface ProviderFormField {
	field: keyof Provider
	label: string
	type: "text" | "dropdown" | "tagpicker"
	options?: IDropdownOption[]
	tags?: ITag[]
	NoResults?: (props: any) => JSX.Element
	noResultsLabel?: string
	showNoResultsButton?: (...args: any[]) => boolean
	required?: boolean
	fetchOptions?: (query: string) => Promise<IDropdownOption[]>
}

const getProviderFormFields = (specialties?: ProviderSpecialty[]): ProviderFormField[] => {
	return [
		{ field: "cw_firstname", label: "First Name", type: "text", required: true },
		{ field: "cw_middlename", label: "Middle Name", type: "text" },
		{ field: "cw_lastname", label: "Last Name", type: "text", required: true },
		{ field: "cw_title", label: "Title", type: "text" },
		{ field: "cw_languagesspoken", label: "Languages Spoken", type: "text" },
		{
			field: "cw_gender",
			label: "Gender",
			type: "dropdown",
			options: [
				{ key: "121640000", text: "Male" },
				{ key: "121640001", text: "Female" }
			]
		},
		{ field: "cw_website", label: "Website", type: "text" },
		{
			field: "specialties",
			label: "Specialties",
			type: "tagpicker",
			tags: convertSpecialtiesToTags(specialties),
			NoResults: props => <ConfirmSpecialtyRequest newSpecialty={props.newSpecialty} setOpen={props.setOpen} />,
			noResultsLabel: "Request New Specialty",
			showNoResultsButton: (alreadyExistingSpecialty: boolean) => !alreadyExistingSpecialty
		}
	]
}

function convertSpecialtiesToTags(specialties?: ProviderSpecialty[]): ITag[] {
	return specialties ? specialties.map(s => ({ key: s.cw_specialtyid, name: s.cw_name })) : []
}

function convertTagsToSpecialties(tags?: ITag[]): ProviderSpecialty[] {
	return tags ? tags.map(t => ({ cw_name: t.name, cw_specialtyid: String(t.key) })) : []
}

function ProviderPanel(props: ProviderPanelProps) {
	const { provider, setOpen, edit, specialties, loading, error } = props
	const [fields, setFields] = useState<Partial<Provider>>(provider || {})
	const [tagPickerText, setTagPickerText] = useState("")
	const [showTagPickerPanel, setShowTagPickerPanel] = useState(false)

	const formFields = getProviderFormFields(specialties)
	const alreadyExistingSpecialty = specialties?.find(s => s.cw_name.toLowerCase() === tagPickerText.toLowerCase())
	const handleChange = (key: string, value: any) => setFields({ ...fields, [key]: value })

	return (
		<Panel
			isOpen
			headerText={`${edit ? "Edit" : "Add"} Provider`}
			onDismiss={() => setOpen("")}
			isFooterAtBottom
			onRenderFooterContent={() => <ProviderPanelFooter {...props} fields={fields} loading={loading} error={error} />}>
			{formFields.map(({ field, required, label, type, options, tags, NoResults, noResultsLabel, showNoResultsButton }) => {
				switch (type) {
					case "text":
						return <TextField required={required} key={label} label={label} value={String(fields[field] || "")} onChange={(_, v) => handleChange(field, v)} />
					case "dropdown":
						return (
							<Dropdown
								disabled={field === "specialties" && (loading || error)}
								key={label}
								label={label}
								options={options}
								selectedKey={String(fields[field] || "")}
								onChange={(_, v) => handleChange(field, v.key)}
							/>
						)
					case "tagpicker":
						return (
							<Stack tokens={{ childrenGap: 4 }} key={label}>
								{/* padding bottom defualts to 5, but TagPicker has extra 4 marginTop that's hard to overwrite, so just reducing padding here to be consistent with label/field spacing of TextField */}
								<Label styles={{ root: { paddingBottom: 1 } }}>Specialties</Label>
								<TagPicker
									selectedItems={convertSpecialtiesToTags(fields[field] as ProviderSpecialty[])}
									onInputChange={q => {
										setTagPickerText(q)
										return q
									}}
									onResolveSuggestions={(q, selected) =>
										q
											? tags.filter(s => !selected.some(ss => s.key === ss.key) && s.name.toLowerCase().includes(q.toLowerCase()))
											: tags.filter(s => !selected.some(ss => s.key === ss.key))
									}
									onChange={items => handleChange(field, convertTagsToSpecialties(items))}
								/>
								{!!tagPickerText && showNoResultsButton(alreadyExistingSpecialty) && (
									<Stack.Item align="end" styles={{ root: { marginTop: "1rem !important" } }}>
										<DefaultButton text={noResultsLabel} onClick={() => setShowTagPickerPanel(true)} />
									</Stack.Item>
								)}
								{showTagPickerPanel && <NoResults newSpecialty={tagPickerText} setOpen={setShowTagPickerPanel} provider={provider} />}
							</Stack>
						)
					default:
						return null
				}
			})}
		</Panel>
	)
}

function prepareFieldsForSending(fields: ProviderCRUD, edit = false, practiceId: string) {
	const cleanedFields: ProviderCRUD = structuredClone(fields)
	cleanedFields.cw_name = `${cleanedFields.cw_firstname} ${cleanedFields.cw_lastname}`
	if (edit) {
		// updating an existing provider: clean null fields, readonly fields, and relationship fields
		for (const field in cleanedFields) {
			if (!cleanedFields[field] || field.includes("@") || field.startsWith("_")) delete cleanedFields[field]
		}
	} else {
		// adding a new provider: set the practice relationship
		cleanedFields["cw_Account@odata.bind"] = `/accounts(${practiceId})`
	}

	return JSON.stringify(cleanedFields)
}

function getSpinnerLabel(loadingProviders: boolean, edit: boolean) {
	if (loadingProviders) return `${edit ? "Updating" : "Adding"} Provider...`
	else return "Loading..."
}

function fieldsValid(fields: Partial<Provider>) {
	return getProviderFormFields()
		.filter(f => f.required)
		.every(f => !!fields[f.field])
}

function ProviderPanelFooter(props: ProviderPanelFooterProps) {
	const { fields, edit, practiceId, provider, setOpen, setProviders, loading: parentLoading, error: parentError } = props
	const body = useMemo(() => prepareFieldsForSending(fields, edit, practiceId), [fields, edit, practiceId])
	const {
		data: providers,
		loading: loadingProviders,
		error: providerError,
		success,
		fetchData: saveProvider
	} = useApiCall<Provider>(`/providers${edit ? `/${provider.cw_practice_providerid}` : ""}`, { method: edit ? "PATCH" : "POST", body, handlerOnly: true, parseJSON: !edit, errorParser: e => e })

	const loading = parentLoading || loadingProviders
	const error = parentError || providerError

	useEffect(() => {
		if (!success) return
		setOpen("")
		setProviders(prev => (edit ? prev.map(p => (p.cw_practice_providerid === providers.cw_practice_providerid ? providers : p)) : [...prev, providers]))
	}, [providers, setOpen, setProviders, success, edit])

	return (
		<>
			{loading && <Spinner label={getSpinnerLabel(loadingProviders, edit)} />}
			{error && <MessageBar messageBarType={MessageBarType.error}>{error.message || JSON.stringify(error)}</MessageBar>}
			<Stack horizontal horizontalAlign="end" styles={{ root: { marginTop: "1rem" } }}>
				<DefaultButton styles={{ root: { marginRight: "2rem" } }} text="Cancel" onClick={() => setOpen("")} />
				<PrimaryButton disabled={loading || !fieldsValid(fields)} text={edit ? "Update" : "Add"} onClick={saveProvider} />
			</Stack>
		</>
	)
}

function RemoveProviderConfirmation({ selectedProvider, setProviders, setShowConfirmation }: RemoveProviderConfirmationProps) {
	const { loading, error, success, fetchData: removeProvider } = useApiCall(`/providers/${selectedProvider.cw_practice_providerid}`, { method: "DELETE", handlerOnly: true })

	useEffect(() => {
		if (!success) return
		setShowConfirmation(false)
		setProviders(prev => prev.filter(p => p.cw_practice_providerid !== selectedProvider.cw_practice_providerid))
	}, [success, setShowConfirmation, setProviders, selectedProvider])

	return (
		<Dialog hidden={false} minWidth={400} onDismiss={() => setShowConfirmation(false)} dialogContentProps={{ title: "Remove Provider" }}>
			<DialogContent styles={{ inner: { padding: "0px 24px 6px" }, header: { display: "none" } }}>
				{loading ? (
					<Spinner label="Removing Provider..." />
				) : error ? (
					<MessageBar messageBarType={MessageBarType.error}>{error.message || JSON.stringify(error)}</MessageBar>
				) : (
					"Are you sure you want to remove this provider?"
				)}
			</DialogContent>
			<DialogFooter>
				<DefaultButton text="Cancel" onClick={() => setShowConfirmation(false)} />
				<DefaultButton
					text="Remove Provider"
					onClick={removeProvider}
					styles={{ root: { backgroundColor: "red", borderColor: "red", color: "#FFF" }, rootHovered: { backgroundColor: "darkred", color: "#FFF" } }}
				/>
			</DialogFooter>
		</Dialog>
	)
}

function ConfirmSpecialtyRequest({ newSpecialty, setOpen }: { newSpecialty: string; setOpen: React.Dispatch<React.SetStateAction<boolean>> }) {
	const { loading, error, success, fetchData } = useApiCall("/specialties", {
		method: "POST",
		body: JSON.stringify({ cw_name: newSpecialty, contactId: getCurrentUserContactId() }),
		handlerOnly: true
	})

	useEffect(() => {
		if (!success) return
		setTimeout(() => setOpen(false), 3000)
	}, [success, setOpen])

	return (
		<Dialog minWidth={400} hidden={false} onDismiss={() => setOpen(false)} dialogContentProps={{ title: "Request New Specialty" }}>
			<DialogContent styles={{ inner: { padding: "0px 24px 6px" }, header: { display: "none" } }}>
				{loading ? (
					<Spinner label="Requesting Specialty..." />
				) : error ? (
					<MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
				) : success ? (
					<MessageBar messageBarType={MessageBarType.success}>Specialty requested successfully</MessageBar>
				) : (
					`Are you sure you want to request new specialty "${newSpecialty}"?`
				)}
			</DialogContent>
			<DialogFooter>
				<DefaultButton text="Cancel" onClick={() => setOpen(false)} />
				<PrimaryButton text="Request New Specialty" onClick={fetchData} />
			</DialogFooter>
		</Dialog>
	)
}
